/**
 * Table Helper Utilities
 *
 * Purpose: Utility functions for table column generation and cell rendering
 * Architecture: Pure functions for MDTable component
 * Dependencies: MUI DataGrid, React, theme utilities
 * Author: Development Team
 * Created: 2025-01-27 (Extracted from MDTable.tsx during refactoring)
 */

import React from 'react';
import { ALL_LABEL, ALL_VALUE } from './listValues';
import { Typography, Chip, Box, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { ViewConfiguration, ViewField } from '../types/table';
import { ValueChipColorKey } from '../theme/orchestrateTheme';
import ValueChip from '../components/common/ValueChip';
import { asList } from './listValues';
import {
  DEFAULT_CURRENCY_CODE,
  formatCurrency,
  formatNumber,
  formatPercentage,
} from './formatting';
import { formatDate, formatDateTime } from './datetime';

/**
 * Helper function to get appropriate column width based on datatype
 */
export const getColumnWidth = (datatype: string, isMultiselect?: boolean): number => {
  switch (datatype.toLowerCase()) {
    case 'boolean':
    case 'bool':
      return 100;
    case 'date':
      return 120;
    case 'datetime':
      return 180;
    case 'currency':
    case 'decimal':
      return 130;
    case 'text':
    case 'string':
      return 200;
    case 'list':
    case 'dropdown':
      // Multiselect lists need more width to display multiple chips/tags
      return isMultiselect ? 225 : 175;
    default:
      return 150;
  }
};

/**
 * Helper function to extract name from a value (handles both strings and objects)
 */
export const getValueName = (value: any): string => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'name' in value) return value.name;
  return String(value || '');
};

/**
 * Helper function to extract chip_color from a value (if it's an object)
 */
export const getValueChipColorKey = (value: any): string | null => {
  if (value && typeof value === 'object' && 'chip_color' in value) {
    return value.chip_color || null;
  }
  return null;
};

/**
 * Helper function to get sortable value for multiselect arrays
 * Returns a string that sorts by first value, then second value, etc.
 */
export const getMultiselectSortValue = (value: any): string => {
  if (!value) return '';
  if (Array.isArray(value)) {
    // Sort by first value, then second value, etc.
    // Join with a separator that ensures proper sorting
    return value.map((v) => String(v || '')).join('|');
  }
  return String(value || '');
};

/**
 * Helper function to get searchable value for multiselect arrays
 * Returns a string containing all values for filtering/searching
 */
export const getMultiselectSearchValue = (value: any): string => {
  if (!value) return '';
  if (Array.isArray(value)) {
    // Join all values for searching
    return value.map((v) => String(v || '')).join(' ');
  }
  return String(value || '');
};

/**
 * Sort comparator for multiselect fields
 * Sorts by first value, then by second value if first values are equal
 */
export const multiselectSortComparator = (v1: any, v2: any): number => {
  const val1 = getMultiselectSortValue(v1);
  const val2 = getMultiselectSortValue(v2);
  return val1.localeCompare(val2);
};

/**
 * Helper function to render multiselect tags with ellipsis for overflow
 * Shows first 2-3 tags, then "+ X more" with tooltip showing all values on cell hover
 * Maintains consistent row height by preventing tag wrapping
 * Values are UUIDs - uses MDM lookup for resolution
 */
export const renderMultiselectTags = (
  values: any[],
  maxVisible: number = 2,
  fielddefId?: string,
  chipColorLookup?: (fielddefId: string, valueName: string) => string | null,
  mdmGetValue?: (fielddefId: string, uuid: string) => string | undefined
): React.ReactNode => {
  if (!Array.isArray(values) || values.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  // Helper to get display name - use MDM lookup if available, fallback to raw value
  const getDisplayName = (v: any): string => {
    // ORCH-418: the reserved ALL value reads "All"
    if (v === ALL_VALUE) return ALL_LABEL;
    if (fielddefId && mdmGetValue && typeof v === 'string') {
      return mdmGetValue(fielddefId, v) || v;
    }
    return getValueName(v);
  };

  // Tooltip content showing all values
  const tooltipContent = (
    <Box>
      {values.map((v, i) => (
        <Typography key={i} variant="caption" sx={{ display: 'block' }}>
          {getDisplayName(v)}
        </Typography>
      ))}
    </Box>
  );

  // If all tags fit, show them all without wrapping, but still wrap in tooltip
  if (values.length <= maxVisible) {
    return (
      <Tooltip
        title={tooltipContent}
        arrow
        placement="top"
        enterDelay={200}
        leaveDelay={0}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ],
        }}
      >
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            gap: 0.5,
            flexWrap: 'nowrap',
            alignItems: 'center',
            overflow: 'hidden',
            width: '100%',
            cursor: 'default',
          }}
        >
          {values.map((v, i) => {
            const valueName = getDisplayName(v);
            // Chip color lookup by resolved name
            const chipColorKey =
              fielddefId && chipColorLookup ? chipColorLookup(fielddefId, valueName) : null;

            return (
              <ValueChip
                key={i}
                label={valueName}
                chipColor={chipColorKey as ValueChipColorKey | null | undefined}
                size="small"
              />
            );
          })}
        </Box>
      </Tooltip>
    );
  }

  // Show first N tags + "+ X more" with tooltip on entire cell
  const visibleTags = values.slice(0, maxVisible);
  const remainingCount = values.length - maxVisible;

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      enterDelay={200}
      leaveDelay={0}
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
        ],
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          gap: 0.5,
          flexWrap: 'nowrap',
          alignItems: 'center',
          overflow: 'hidden',
          width: '100%',
          cursor: 'default',
        }}
      >
        {visibleTags.map((v, i) => {
          const valueName = getDisplayName(v);
          // Chip color lookup by resolved name
          const chipColorKey =
            fielddefId && chipColorLookup ? chipColorLookup(fielddefId, valueName) : null;

          return (
            <ValueChip
              key={i}
              label={valueName}
              chipColor={chipColorKey as ValueChipColorKey | null | undefined}
              size="small"
            />
          );
        })}
        <Chip label={`+ ${remainingCount} more`} size="small" color="default" variant="outlined" />
      </Box>
    </Tooltip>
  );
};

/**
 * Helper function to render field value based on datatype
 *
 * Numbers, currency, percentages, dates and times format in the user's locale
 * (ORCH-191) through the shared formatters. The locale controls presentation only:
 * the currency symbol comes from `currencyCode` — the record's own currency,
 * resolved per row by MDTable — never from the user's locale.
 */
export const renderFieldValue = (
  value: any,
  datatype: string,
  isMultiselect: boolean,
  field?: ViewField,
  chipColorLookup?: (fielddefId: string, valueName: string) => string | null,
  mdmGetValue?: (fielddefId: string, uuid: string) => string | undefined,
  currencyCode?: string | null
): React.ReactNode => {
  if (value === null || value === undefined) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  // Use resolved display decimal places from field (resolved by backend)
  const displayDecimalPlaces = field?.display_decimal_places ?? 2;

  switch (datatype.toLowerCase()) {
    case 'text':
    case 'string':
      return <Typography variant="body2">{value}</Typography>;

    case 'currency':
      return (
        <Typography variant="body2">
          {formatCurrency(Number(value), currencyCode || DEFAULT_CURRENCY_CODE, undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: displayDecimalPlaces,
          })}
        </Typography>
      );

    case 'decimal':
    case 'number':
      return (
        <Typography variant="body2">
          {formatNumber(Number(value), displayDecimalPlaces, undefined, {
            minimumFractionDigits: 0,
          })}
        </Typography>
      );

    case 'percent':
      // Stored as a fraction (0.45); the formatter multiplies for display (45%)
      return (
        <Typography variant="body2">
          {formatPercentage(Number(value), displayDecimalPlaces)}
        </Typography>
      );

    case 'date':
      return <Typography variant="body2">{formatDate(value, 'medium')}</Typography>;

    case 'datetime':
      return <Typography variant="body2">{formatDateTime(value)}</Typography>;

    case 'boolean':
    case 'bool':
      return (
        <Chip label={value ? 'Yes' : 'No'} size="small" color={value ? 'success' : 'default'} />
      );

    case 'list':
    case 'dropdown': {
      // The stored value may be in either shape (ORCH-354); show tags when the field is
      // multi-select or the row holds several values, otherwise the single value
      // Items may be UUID strings or resolved { name } objects, so arrays keep their items
      const isObjectValue = typeof value === 'object' && !Array.isArray(value);
      const items: any[] = Array.isArray(value)
        ? value.filter((v) => v !== null && v !== undefined && v !== '')
        : isObjectValue
          ? [value]
          : asList(value);
      if (isMultiselect || items.length > 1) {
        return renderMultiselectTags(items, 2, field?.fielddef_id, chipColorLookup, mdmGetValue);
      }
      {
        // Single value - use MDM lookup if available, otherwise display raw value
        const single = isObjectValue ? value : items[0];
        let displayName: string;
        if (field?.fielddef_id && mdmGetValue && typeof single === 'string') {
          displayName = mdmGetValue(field.fielddef_id, single) || single;
        } else {
          displayName = getValueName(single);
        }
        // Chip color lookup by resolved name
        const chipColorKey =
          field?.fielddef_id && chipColorLookup
            ? chipColorLookup(field.fielddef_id, displayName)
            : null;

        return (
          <ValueChip
            label={displayName}
            chipColor={chipColorKey as ValueChipColorKey | null | undefined}
            size="small"
          />
        );
      }
    }

    default:
      return <Typography variant="body2">{String(value)}</Typography>;
  }
};

/**
 * Helper function to extract field value from a row
 * Returns raw UUIDs from meta_data - resolution happens via MDM cache
 */
export const getFieldValueFromRow = (row: any, fielddefId: string): any => {
  if (!row || !fielddefId) return undefined;

  // Check meta_data (primary location for field values)
  if (row.meta_data && row.meta_data[fielddefId] !== undefined) {
    return row.meta_data[fielddefId];
  }
  // Fallback to metadata (alternate casing)
  if (row.metadata && row.metadata[fielddefId] !== undefined) {
    return row.metadata[fielddefId];
  }
  // Direct property access
  return row[fielddefId];
};

/**
 * Generate GridColDef columns from view configuration
 */
export const generateColumnsFromView = (
  viewConfig: ViewConfiguration,
  customRenderers?: Record<string, any>,
  chipColorLookup?: (fielddefId: string, valueName: string) => string | null,
  mdmGetValue?: (fielddefId: string, uuid: string) => string | undefined,
  _mdmLoading?: boolean,
  /** The ISO 4217 code a row's currency cells format with (its fd_currency), if known. */
  currencyCodeForRow?: (row: any) => string | undefined
): GridColDef[] => {
  try {
    if (!viewConfig || !viewConfig.fields || !Array.isArray(viewConfig.fields)) {
      console.error('MDTable: Invalid viewConfig in generateColumnsFromView', viewConfig);
      return [];
    }

    return viewConfig.fields
      .filter((field: ViewField) => {
        // Skip fields without field_definition
        if (!field.field_definition) {
          console.warn('MDTable: Field missing field_definition, skipping:', field.fielddef_id);
          return false;
        }
        return true;
      })
      .map((field: ViewField) => {
        const { field_definition, label_override, is_required: _is_required } = field;
        // Use fallback values if field_definition is missing (defensive programming)
        const label =
          label_override || field_definition?.label || field.field_label || field.fielddef_id;
        const datatype =
          field_definition?.datatype || field.field_datatype || field.datatype || 'text';
        const isMultiselect = field_definition?.is_multiselect ?? field.is_multiselect ?? false;

        // Map metadata datatype to DataGridPro column type for correct sorting/filtering
        const dtLower = datatype.toLowerCase();
        const gridType = (() => {
          switch (dtLower) {
            case 'number':
            case 'integer':
            case 'decimal':
            case 'currency':
              return 'number' as const;
            case 'date':
              return 'date' as const;
            case 'datetime':
              return 'dateTime' as const;
            case 'boolean':
              return 'boolean' as const;
            default:
              return 'string' as const;
          }
        })();

        // IMPORTANT: Always use fielddef_id for column field name (data operations)
        // field.field_id is for metadata system tracking only
        return {
          field: field.fielddef_id, // Use fielddef_id for data operations
          headerName: label,
          type: gridType,
          width: getColumnWidth(datatype, isMultiselect),
          minWidth: getColumnWidth(datatype, isMultiselect), // Minimum width
          // Use flex for text/string columns to allow them to grow, fixed width for others
          ...(datatype.toLowerCase() === 'text' || datatype.toLowerCase() === 'string'
            ? { flex: 1 }
            : {}),
          sortable: true,
          filterable: true,
          resizable: true, // Enable column resizing
          // Allow pointer events for tooltips to work in multiselect columns
          disablePointerEvents: false,
          // MUI X DataGrid v8 valueGetter signature: (value, row, column, apiRef)
          valueGetter: (_value: any, row: any) => {
            if (!row) return null;

            try {
              const value = getFieldValueFromRow(row, field.fielddef_id);

              // For multiselect list fields, resolve UUIDs to display names for sorting/filtering
              if (
                isMultiselect &&
                (datatype.toLowerCase() === 'list' || datatype.toLowerCase() === 'dropdown')
              ) {
                if (mdmGetValue) {
                  return asList(value)
                    .map((v) => mdmGetValue(field.fielddef_id, v) || v)
                    .join(' ');
                }
                return getMultiselectSearchValue(value);
              }
              // For single-select list fields, resolve UUID to display name for sorting
              if (
                !isMultiselect &&
                (datatype.toLowerCase() === 'list' || datatype.toLowerCase() === 'dropdown')
              ) {
                // A single-select field may still hold a one-element array (ORCH-354)
                const items = asList(value);
                if (mdmGetValue && items.length > 0) {
                  return items.map((v) => mdmGetValue(field.fielddef_id, v) || v).join(' ');
                }
              }

              // Coerce values to correct types for sorting
              if (value != null && gridType === 'number') {
                const num = Number(value);
                return isNaN(num) ? null : num;
              }
              if (value != null && (gridType === 'date' || gridType === 'dateTime')) {
                const d = new Date(value);
                return isNaN(d.getTime()) ? null : d;
              }

              return value;
            } catch (error) {
              console.error('MDTable: Error in valueGetter:', error, {
                field: field.fielddef_id,
                row,
              });
              return null;
            }
          },
          sortComparator:
            isMultiselect &&
            (datatype.toLowerCase() === 'list' || datatype.toLowerCase() === 'dropdown')
              ? (v1: any, v2: any) => {
                  // For multiselect, v1/v2 are the searchable strings from valueGetter
                  // Compare them as strings (space-joined display names)
                  const str1 = String(v1 || '');
                  const str2 = String(v2 || '');
                  return str1.localeCompare(str2);
                }
              : undefined,
          renderCell: (params: any) => {
            try {
              // Handle cases where params might be undefined
              if (!params || !params.row) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                );
              }

              // Get the actual value for rendering (not the searchable string)
              const actualValue = getFieldValueFromRow(params.row, field.fielddef_id);

              // Check for custom renderer first
              const customRenderer = customRenderers?.[field.fielddef_id];
              if (customRenderer) {
                return customRenderer(actualValue, params.row);
              }

              // Use default renderer based on datatype (pass field for decimal places)
              // Note: chipColorLookup and mdmGetValue will be passed via closure from generateColumnsFromView
              // This handles all datatypes including list fields with chip colors
              return renderFieldValue(
                actualValue,
                datatype,
                isMultiselect,
                field,
                chipColorLookup,
                mdmGetValue,
                currencyCodeForRow?.(params.row)
              );
            } catch (error) {
              console.error('MDTable: Error rendering cell:', error, {
                field: field.fielddef_id,
                row: params.row,
                datatype,
              });
              return (
                <Typography variant="body2" color="error">
                  Error rendering
                </Typography>
              );
            }
          },
        };
      });
  } catch (error) {
    console.error('MDTable: Error in generateColumnsFromView:', error, { viewConfig });
    return [];
  }
};
